import { useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, Check, X, RefreshCcw, ExternalLink, Building2, User } from "lucide-react";

const DOC_LABELS = { passport: "Passport", driving_licence: "Driving licence", national_id: "National ID", companies_house: "Companies House certificate", business_registration: "Business registration / VAT" };
const toAbsolute = (url) => (!url || url.startsWith("http") ? url : `${BACKEND_URL}${url}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Row({ row, onReview }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const doc = row.identity_document || {};
  const review = async (approve) => {
    if (!approve && !reason.trim()) { toast.error("Add a short reason for the rejection"); return; }
    setBusy(true);
    try { await onReview(row.id, approve, reason); } finally { setBusy(false); }
  };
  const isImage = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(doc.url || "") || (doc.url || "").includes("/api/files/");
  return (
    <div className="glass rounded-2xl p-5 grid md:grid-cols-[180px_1fr] gap-5" data-testid={`identity-row-${row.id}`}>
      <a href={toAbsolute(doc.url)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden bg-black/40 hairline aspect-[4/3] flex items-center justify-center" data-testid={`identity-doc-link-${row.id}`}>
        {isImage ? <img src={toAbsolute(doc.url)} alt="Document" className="w-full h-full object-contain" /> : <span className="text-xs text-zinc-400 inline-flex items-center gap-1">Open document <ExternalLink className="h-3 w-3" /></span>}
      </a>
      <div className="space-y-3 min-w-0">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {row.role === "brand" ? <Building2 className="h-4 w-4 text-nexus-blue" /> : <User className="h-4 w-4 text-nexus-pink" />}
              <span className="font-semibold text-white truncate">{row.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{row.role}</span>
            </div>
            <div className="text-xs text-zinc-400 truncate">{row.email}</div>
          </div>
          <div className="text-right text-[11px] text-zinc-500">
            <div>{DOC_LABELS[doc.document_type] || doc.document_type}</div>
            <div>Uploaded {fmt(doc.uploaded_at)}</div>
          </div>
        </div>
        {doc.status === "pending" ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => review(true)} disabled={busy} className="rounded-full h-9 bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/25" data-testid={`identity-approve-${row.id}`}>
              <Check className="h-4 w-4 mr-1.5" /> Approve
            </Button>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reject reason (sent to user)" className="bg-white/[0.02] border-white/10 text-white h-9 flex-1 min-w-[200px]" data-testid={`identity-reason-${row.id}`} />
            <Button onClick={() => review(false)} disabled={busy} variant="outline" className="rounded-full h-9 border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid={`identity-reject-${row.id}`}>
              <X className="h-4 w-4 mr-1.5" /> Reject
            </Button>
          </div>
        ) : (
          <div className={`text-xs ${doc.status === "approved" ? "text-emerald-300" : "text-red-300"}`} data-testid={`identity-status-${row.id}`}>
            {doc.status === "approved" ? "Approved" : `Rejected — ${doc.reject_reason || "no reason"}`} · {fmt(doc.reviewed_at)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminIdentity() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try { const r = await api.get("/admin/identity-documents", { params: { status: s } }); setRows(r.data?.rows || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const onReview = async (userId, approve, reason) => {
    try {
      await api.post(`/admin/identity-documents/${userId}/review`, { approve, reason });
      toast.success(approve ? "Approved — user notified, briefs now live" : "Rejected — user notified");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Review failed"); }
  };

  return (
    <BrandShell>
      <SEO title="Identity review · Admin" description="Approve brand and creator identity documents." path="/admin/identity" />
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10 py-8 space-y-6" data-testid="admin-identity-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-nexus-pink" /> Admin</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Identity verification</h1>
            <p className="text-zinc-400 mt-2 text-sm">Brand briefs stay hidden from creators until the brand's ID or company document is approved here.</p>
          </div>
          <Button onClick={() => load()} variant="outline" className="rounded-full border-white/10 text-white hover:bg-white/5" data-testid="identity-refresh-btn">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        <div className="flex gap-2" data-testid="identity-status-tabs">
          {["pending", "approved", "rejected"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest border transition-colors ${status === s ? "nexus-gradient text-white border-transparent" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`identity-tab-${s}`}>{s}</button>
          ))}
        </div>
        {loading ? <div className="glass rounded-2xl p-12 text-center text-zinc-500">Loading…</div>
          : rows.length === 0 ? <div className="glass rounded-2xl p-12 text-center text-zinc-500" data-testid="identity-empty">Nothing {status} right now.</div>
          : <div className="space-y-3" data-testid="identity-list">{rows.map((r) => <Row key={r.id} row={r} onReview={onReview} />)}</div>}
      </div>
    </BrandShell>
  );
}
